import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Tag, Search, X } from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';
import Select from '@/components/ui/Select';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

type DiscountType = 'percentage' | 'fixed_amount' | 'free_shipping';
type AppliesTo = 'all' | 'specific_products' | 'specific_categories';

interface CouponFormData {
  code: string;
  discount_type: DiscountType;
  discount_value: string;
  minimum_order_amount: string;
  maximum_discount_amount: string;
  usage_limit: string;
  per_customer_limit: string;
  starts_at: string;
  expires_at: string;
  is_active: boolean;
  applies_to: AppliesTo;
  applicable_ids: string[];
  description: string;
}

/**
 * The server's row (routes/admin/coupons.ts returns it bare, not wrapped).
 * Field names mirror migration 003 — the editor used to post `max_uses` /
 * `max_uses_per_user`, which the strict server schema rejected, so no coupon
 * could be saved from this screen at all.
 */
interface Coupon {
  id: string;
  code: string;
  discount_type: DiscountType;
  discount_value: number | string;
  minimum_order_amount: number | string | null;
  maximum_discount_amount: number | string | null;
  usage_limit: number | null;
  usage_count: number;
  per_customer_limit: number | null;
  starts_at: string | null;
  expires_at: string | null;
  is_active: boolean;
  applies_to: AppliesTo;
  applicable_ids: string[] | null;
  description: string | null;
}

interface PickerOption {
  id: string;
  name: string;
}

interface CategoryNode {
  id: string;
  name: string;
  children?: CategoryNode[];
}

/** `datetime-local` value (local time, no zone) for an ISO timestamp. */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** ISO (UTC) timestamp for a `datetime-local` value — what the server's z.datetime() accepts. */
function toIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function flattenCategories(nodes: CategoryNode[], depth = 0, out: PickerOption[] = []): PickerOption[] {
  for (const n of nodes) {
    out.push({ id: n.id, name: `${'— '.repeat(depth)}${n.name}` });
    if (n.children?.length) flattenCategories(n.children, depth + 1, out);
  }
  return out;
}

export default function CouponEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = id !== 'new';

  const [formData, setFormData] = useState<CouponFormData>({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    minimum_order_amount: '',
    maximum_discount_amount: '',
    usage_limit: '',
    per_customer_limit: '1',
    starts_at: '',
    expires_at: '',
    is_active: true,
    applies_to: 'all',
    applicable_ids: [],
    description: '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof CouponFormData, string>>>({});
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [usageCount, setUsageCount] = useState<number>(0);

  // Applies-to pickers: selected ids resolve to names for display; products
  // are searched (same endpoint the bundle editor uses), categories listed.
  const [selectedNames, setSelectedNames] = useState<Record<string, string>>({});
  const [categories, setCategories] = useState<PickerOption[]>([]);
  const [productQuery, setProductQuery] = useState('');
  const [productResults, setProductResults] = useState<PickerOption[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isEditMode) {
      fetchCoupon();
    }
  }, [id]);

  useEffect(() => {
    if (formData.applies_to !== 'specific_categories' || categories.length > 0) return;
    api
      .get<{ categories: CategoryNode[] }>('/admin/categories')
      .then((res) => setCategories(flattenCategories(res.categories ?? [])))
      .catch(() => setCategories([]));
  }, [formData.applies_to, categories.length]);

  async function fetchCoupon() {
    try {
      setIsLoading(true);
      setLoadError(null);
      const coupon = await api.get<Coupon>(`/admin/coupons/${id}`);

      setFormData({
        code: coupon.code,
        discount_type: coupon.discount_type,
        discount_value: String(coupon.discount_value ?? ''),
        minimum_order_amount: coupon.minimum_order_amount != null ? String(coupon.minimum_order_amount) : '',
        maximum_discount_amount: coupon.maximum_discount_amount != null ? String(coupon.maximum_discount_amount) : '',
        usage_limit: coupon.usage_limit != null ? String(coupon.usage_limit) : '',
        per_customer_limit: coupon.per_customer_limit != null ? String(coupon.per_customer_limit) : '',
        starts_at: toLocalInput(coupon.starts_at),
        expires_at: toLocalInput(coupon.expires_at),
        is_active: coupon.is_active,
        applies_to: coupon.applies_to ?? 'all',
        applicable_ids: coupon.applicable_ids ?? [],
        description: coupon.description || '',
      });
      setUsageCount(coupon.usage_count ?? 0);
    } catch (err) {
      setLoadError('Failed to load coupon. Please try again.');
      console.error('Error fetching coupon:', err);
    } finally {
      setIsLoading(false);
    }
  }

  const searchProducts = useCallback((query: string) => {
    setProductQuery(query);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setProductResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await api.get<{ products: PickerOption[] }>(
          `/admin/products?q=${encodeURIComponent(query)}&limit=8`,
        );
        setProductResults(data.products);
      } catch {
        setProductResults([]);
      }
    }, 300);
  }, []);

  function addApplicable(option: PickerOption) {
    if (formData.applicable_ids.includes(option.id)) return;
    setSelectedNames((prev) => ({ ...prev, [option.id]: option.name }));
    setFormData((prev) => ({ ...prev, applicable_ids: [...prev.applicable_ids, option.id] }));
    setProductQuery('');
    setProductResults([]);
  }

  function removeApplicable(optionId: string) {
    setFormData((prev) => ({ ...prev, applicable_ids: prev.applicable_ids.filter((x) => x !== optionId) }));
  }

  function validateForm(): boolean {
    const newErrors: Partial<Record<keyof CouponFormData, string>> = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Coupon code is required';
    }

    const value = parseFloat(formData.discount_value);
    if (formData.discount_type !== 'free_shipping') {
      if (!formData.discount_value || !(value > 0)) {
        newErrors.discount_value = 'Discount value must be greater than 0';
      } else if (formData.discount_type === 'percentage' && value > 100) {
        newErrors.discount_value = 'Percentage cannot exceed 100';
      }
    }

    if (formData.minimum_order_amount && parseFloat(formData.minimum_order_amount) < 0) {
      newErrors.minimum_order_amount = 'Minimum order amount cannot be negative';
    }

    if (formData.maximum_discount_amount && parseFloat(formData.maximum_discount_amount) < 0) {
      newErrors.maximum_discount_amount = 'Maximum discount cannot be negative';
    }

    if (formData.usage_limit && parseInt(formData.usage_limit, 10) < 1) {
      newErrors.usage_limit = 'Max uses must be at least 1 (leave empty for unlimited)';
    }

    if (formData.per_customer_limit && parseInt(formData.per_customer_limit, 10) < 1) {
      newErrors.per_customer_limit = 'Max uses per customer must be at least 1 (leave empty for unlimited)';
    }

    if (formData.starts_at && formData.expires_at) {
      const start = new Date(formData.starts_at);
      const end = new Date(formData.expires_at);
      if (end < start) {
        newErrors.expires_at = 'Expiration date must be after start date';
      }
    }

    if (formData.applies_to !== 'all' && formData.applicable_ids.length === 0) {
      newErrors.applies_to = `Pick at least one ${formData.applies_to === 'specific_products' ? 'product' : 'category'}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSaving(true);
      // Field names + ISO dates match the server's strict schema exactly;
      // empty optional fields are sent as null so an edit can CLEAR them.
      const payload = {
        code: formData.code.toUpperCase(),
        discount_type: formData.discount_type,
        discount_value: formData.discount_type === 'free_shipping' ? 0 : parseFloat(formData.discount_value),
        minimum_order_amount: formData.minimum_order_amount ? parseFloat(formData.minimum_order_amount) : null,
        maximum_discount_amount: formData.maximum_discount_amount ? parseFloat(formData.maximum_discount_amount) : null,
        usage_limit: formData.usage_limit ? parseInt(formData.usage_limit, 10) : null,
        per_customer_limit: formData.per_customer_limit ? parseInt(formData.per_customer_limit, 10) : null,
        starts_at: toIso(formData.starts_at),
        expires_at: toIso(formData.expires_at),
        is_active: formData.is_active,
        applies_to: formData.applies_to,
        applicable_ids: formData.applies_to === 'all' ? null : formData.applicable_ids,
        description: formData.description || null,
      };

      if (isEditMode) {
        await api.put(`/admin/coupons/${id}`, payload);
      } else {
        await api.post('/admin/coupons', payload);
      }

      navigate('/admin/coupons');
    } catch (err: any) {
      // lib/api throws Error(message || error), so the server's text is here.
      const errorMessage = err?.message || 'Failed to save coupon. Please try again.';
      setErrors({ code: errorMessage });
      console.error('Error saving coupon:', err);
    } finally {
      setIsSaving(false);
    }
  }

  function handleInputChange(field: keyof CouponFormData, value: string | boolean) {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen" role="status" aria-live="polite">
        <LoadingSpinner />
        <span className="sr-only">Loading coupon...</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="p-6 bg-red-50 border border-red-200 rounded-lg" role="alert">
          <p className="text-red-800">{loadError}</p>
          <Button
            variant="outline"
            onClick={() => navigate('/admin/coupons')}
            className="mt-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" aria-hidden="true" />
            Back to Coupons
          </Button>
        </div>
      </div>
    );
  }

  const isFreeShipping = formData.discount_type === 'free_shipping';

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/admin/coupons')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" aria-hidden="true" />
          Back to Coupons
        </Button>
        <div className="flex items-center gap-3">
          <Tag className="w-8 h-8 text-gray-700" aria-hidden="true" />
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditMode ? 'Edit Coupon' : 'Create Coupon'}
          </h1>
        </div>
        {isEditMode && (
          <p className="mt-2 text-sm text-gray-600">
            Redeemed {usageCount} time{usageCount === 1 ? '' : 's'}
            {formData.usage_limit ? ` of ${formData.usage_limit}` : ''}.
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <Card title="Coupon Details" padding="lg">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Coupon Code"
                value={formData.code}
                onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                error={errors.code}
                placeholder="SUMMER2026"
                required
                aria-required="true"
                className="font-mono"
              />

              <Select
                label="Discount Type"
                value={formData.discount_type}
                onChange={(e) => handleInputChange('discount_type', e.target.value as DiscountType)}
                options={[
                  { label: 'Percentage', value: 'percentage' },
                  { label: 'Fixed Amount', value: 'fixed_amount' },
                  { label: 'Free Shipping', value: 'free_shipping' },
                ]}
                required
                aria-required="true"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {!isFreeShipping && (
                <Input
                  label="Discount Value"
                  type="number"
                  value={formData.discount_value}
                  onChange={(e) => handleInputChange('discount_value', e.target.value)}
                  error={errors.discount_value}
                  placeholder={formData.discount_type === 'percentage' ? '10' : '5.00'}
                  helperText={formData.discount_type === 'percentage' ? 'Enter percentage (1-100)' : 'Enter dollar amount'}
                  required
                  aria-required="true"
                  step={formData.discount_type === 'percentage' ? '1' : '0.01'}
                />
              )}

              <Input
                label="Minimum Order Amount"
                type="number"
                value={formData.minimum_order_amount}
                onChange={(e) => handleInputChange('minimum_order_amount', e.target.value)}
                error={errors.minimum_order_amount}
                placeholder="0.00"
                helperText="Optional minimum for the items the coupon applies to"
                step="0.01"
              />

              {formData.discount_type === 'percentage' && (
                <Input
                  label="Maximum Discount"
                  type="number"
                  value={formData.maximum_discount_amount}
                  onChange={(e) => handleInputChange('maximum_discount_amount', e.target.value)}
                  error={errors.maximum_discount_amount}
                  placeholder="Leave empty for no cap"
                  helperText="Optional dollar cap on a percentage discount"
                  step="0.01"
                />
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Max Total Uses"
                type="number"
                value={formData.usage_limit}
                onChange={(e) => handleInputChange('usage_limit', e.target.value)}
                error={errors.usage_limit}
                placeholder="Leave empty for unlimited"
                helperText="Total times coupon can be used"
                step="1"
              />

              <Input
                label="Max Uses Per Customer"
                type="number"
                value={formData.per_customer_limit}
                onChange={(e) => handleInputChange('per_customer_limit', e.target.value)}
                error={errors.per_customer_limit}
                placeholder="Leave empty for unlimited"
                helperText="Times each signed-in customer can use it"
                step="1"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Start Date"
                type="datetime-local"
                value={formData.starts_at}
                onChange={(e) => handleInputChange('starts_at', e.target.value)}
                helperText="Optional start date and time"
              />

              <Input
                label="Expiration Date"
                type="datetime-local"
                value={formData.expires_at}
                onChange={(e) => handleInputChange('expires_at', e.target.value)}
                error={errors.expires_at}
                helperText="Optional expiration date and time"
              />
            </div>

            <Select
              label="Applies To"
              value={formData.applies_to}
              onChange={(e) => {
                handleInputChange('applies_to', e.target.value as AppliesTo);
                setFormData((prev) => ({ ...prev, applicable_ids: [] }));
              }}
              options={[
                { label: 'All Products', value: 'all' },
                { label: 'Specific Products', value: 'specific_products' },
                { label: 'Specific Categories', value: 'specific_categories' },
              ]}
              error={errors.applies_to}
            />

            {formData.applies_to !== 'all' && (
              <div className="space-y-2" data-testid="applicable-picker">
                {formData.applicable_ids.length > 0 && (
                  <ul className="flex flex-wrap gap-2">
                    {formData.applicable_ids.map((optionId) => (
                      <li
                        key={optionId}
                        className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-800"
                      >
                        {selectedNames[optionId] ?? categories.find((c) => c.id === optionId)?.name ?? optionId}
                        <button
                          type="button"
                          onClick={() => removeApplicable(optionId)}
                          aria-label={`Remove ${selectedNames[optionId] ?? optionId}`}
                          className="text-gray-500 hover:text-gray-800"
                        >
                          <X className="w-3 h-3" aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                {formData.applies_to === 'specific_products' ? (
                  <div className="relative">
                    <Input
                      label="Add a product"
                      value={productQuery}
                      onChange={(e) => searchProducts(e.target.value)}
                      placeholder="Search products by name or SKU"
                      helperText="Only these products count toward the discount"
                    />
                    <Search className="pointer-events-none absolute right-3 top-9 w-4 h-4 text-gray-400" aria-hidden="true" />
                    {productResults.length > 0 && (
                      <ul className="mt-1 max-h-56 overflow-auto rounded-md border border-gray-200 bg-white shadow-sm">
                        {productResults.map((p) => (
                          <li key={p.id}>
                            <button
                              type="button"
                              onClick={() => addApplicable(p)}
                              className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                            >
                              {p.name}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  <Select
                    label="Add a category"
                    value=""
                    onChange={(e) => {
                      const cat = categories.find((c) => c.id === e.target.value);
                      if (cat) addApplicable(cat);
                    }}
                    options={[
                      { label: categories.length ? 'Choose a category…' : 'Loading categories…', value: '' },
                      ...categories
                        .filter((c) => !formData.applicable_ids.includes(c.id))
                        .map((c) => ({ label: c.name, value: c.id })),
                    ]}
                  />
                )}
              </div>
            )}

            <Textarea
              label="Description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Internal notes about this coupon..."
              helperText="Optional internal description"
              rows={3}
            />

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => handleInputChange('is_active', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
              <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                Active (coupon can be used immediately)
              </label>
            </div>
          </div>
        </Card>

        <div className="mt-6 flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/coupons')}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={isSaving}
          >
            <Save className="w-4 h-4 mr-2" aria-hidden="true" />
            {isEditMode ? 'Update Coupon' : 'Create Coupon'}
          </Button>
        </div>
      </form>
    </div>
  );
}
